import _, { debounce } from 'lodash';
import React, { useMemo } from 'react';
import {
  CurrencyInput,
  MemberFilter,
  OpportunityStageCategoryFilter,
  OpportunityStageFilter,
  OpportunityTypeFilter,
  PracticeFilter,
  YesNoFilter,
} from '~/components';
import OpportunityPipelineFilter from '~/components/filters/OpportunityPipelineFilter';
import OpportunityTagFilter from '~/components/filters/OpportunityTagFilter';
import SalesforceRecordTypeFilter from '~/components/filters/SalesforceRecordTypeFilter';
import { useIntegrations } from '~/contexts';
import { useFeatures } from '~/hooks';

function OpportunityFiltersBar({ filters, sticky, onChange }) {
  const features = useFeatures();

  const visible = useMemo(() => {
    const isVisible = (key) => sticky?.includes(key) || !_.isEmpty(filters[key]);
    const isNumberVisible = (key) => sticky?.includes(key) || _.isNumber(filters[key]);

    return {
      opportunityAmountGreaterThanOrEqual: isNumberVisible('opportunityAmountGreaterThanOrEqual'),
      opportunityOwners: isVisible('opportunityOwners'),
      opportunityPipelines: isVisible('opportunityPipelines'),
      opportunityPractices: features.practices && isVisible('opportunityPractices'),
      opportunityStages: isVisible('opportunityStages'),
      opportunityStageCategories: isVisible('opportunityStageCategories'),
      opportunityTags: isVisible('opportunityTags'),
      opportunityTypes: isVisible('opportunityTypes'),
      opportunitySalesforceSynced: isVisible('opportunitySalesforceSynced'),
      opportunitySalesforceRecordTypes: isVisible('opportunitySalesforceRecordTypes'),
      opportunityHubspotSynced: isVisible('opportunityHubspotSynced'),
    };
  }, [filters, sticky, features]);

  const integrations = useIntegrations();
  const salesforceEnabled = integrations?.salesforce;
  const hubspotEnabled = integrations?.hubspot;

  return (
    <>
      {visible.opportunityPipelines && (
        <OpportunityPipelineFilter
          name="opportunityPipelines"
          value={filters.opportunityPipelines}
          placeholder="Opportunity Pipeline"
          onChange={({ target: { value } }) => onChange({ opportunityPipelines: value })}
        />
      )}

      {salesforceEnabled && visible.opportunitySalesforceSynced && (
        <YesNoFilter
          icon="filter"
          clearable
          placeholder="Salesforce Synced"
          value={filters.opportunitySalesforceSynced}
          onChange={({ target: { value } }) => onChange({ opportunitySalesforceSynced: value })}
        />
      )}

      {salesforceEnabled && visible.opportunitySalesforceRecordTypes && (
        <SalesforceRecordTypeFilter
          name="opportunitySalesforceRecordTypes"
          value={filters.opportunitySalesforceRecordTypes}
          onChange={({ target: { value } }) => onChange({ opportunitySalesforceRecordTypes: value })}
        />
      )}

      {hubspotEnabled && visible.opportunityHubspotSynced && (
        <YesNoFilter
          icon="filter"
          clearable
          placeholder="HubSpot Synced"
          value={filters.opportunityHubspotSynced}
          onChange={({ target: { value } }) => onChange({ opportunityHubspotSynced: value })}
        />
      )}

      {visible.opportunityAmountGreaterThanOrEqual && (
        <CurrencyInput
          name="opportunityAmountGreaterThanOrEqual"
          placeholder="Minimum Opportunity Amount"
          value={filters.opportunityAmountGreaterThanOrEqual}
          onChange={debounce((value) => {
            onChange({ opportunityAmountGreaterThanOrEqual: value });
          }, 500)}
        />
      )}

      {visible.opportunityOwners && (
        <MemberFilter
          value={filters.opportunityOwners}
          name="opportunityOwners"
          placeholder="Opportunity Owner"
          onChange={({ target: { value } }) => onChange({ opportunityOwners: value })}
        />
      )}

      {visible.opportunityPractices && (
        <PracticeFilter
          name="opportunityPractices"
          value={filters.opportunityPractices}
          placeholder="Practice"
          onChange={({ target: { value } }) => onChange({ opportunityPractices: value })}
        />
      )}

      {visible.opportunityStages && (
        <OpportunityStageFilter
          name="opportunityStages"
          value={filters.opportunityStages}
          opportunityStageCategoryId={filters.opportunityStageCategories?.map((status) => status.id)}
          opportunityPipelineId={filters.opportunityPipelines?.map((pipeline) => pipeline.id)}
          onChange={({ target: { value } }) => onChange({ opportunityStages: value })}
        />
      )}

      {visible.opportunityStageCategories && (
        <OpportunityStageCategoryFilter
          name="opportunityStageCategories"
          value={filters.opportunityStageCategories}
          onChange={({ target: { value } }) => onChange({ opportunityStageCategories: value })}
        />
      )}

      {visible.opportunityTags && (
        <OpportunityTagFilter
          name="opportunityTags"
          placeholder="Opportunity Tags"
          value={filters.opportunityTags}
          onChange={({ target: { value } }) => onChange({ opportunityTags: value })}
        />
      )}

      {visible.opportunityTypes && (
        <OpportunityTypeFilter
          name="opportunityTypes"
          value={filters.opportunityTypes}
          placeholder="Opportunity Type"
          onChange={({ target: { value } }) => onChange({ opportunityTypes: value })}
        />
      )}
    </>
  );
}

export default OpportunityFiltersBar;
